/* eslint-disable @typescript-eslint/no-explicit-any */
import { ChangeDetectionStrategy, Component, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { InfoCardConfig } from '@vsolv/vectors-ui/info-card';
import { Link } from '@wsphere/links/domain';
import { LinksWebService } from '@wsphere/links/web';
import { SecurityService } from '@wsphere/staff/web';
import { Claim, ClaimItem } from '@wsphere/warranties/domain';
import { BehaviorSubject, combineLatest, map, switchMap, tap } from 'rxjs';
import { ClaimService } from '../../services';
import { ClaimItemHoldsComponent } from '../claim-item-holds/claim-item-holds.component';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'ws-claim-item-details',
  templateUrl: './claim-item-details.component.html',
  standalone: false,
})
export class ClaimItemDetailsComponent {
  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private claimSvc: ClaimService,
    private linkSvc: LinksWebService,
    private securitySvc: SecurityService
  ) {}

  @ViewChild(ClaimItemHoldsComponent) holdsPanel?: ClaimItemHoldsComponent;

  open = Claim.Status.OPEN;
  draft = ClaimItem.Status.DRAFT;
  usesNewFlow = Claim.Authorization.usesNewFlow;
  resolved = ClaimItem.Status.RESOLVED;

  itemId: string | null = null;

  newClaim = new BehaviorSubject<Claim.Model | null>(null);

  readonly item$ = this.route.data.pipe(map(data => data['item'] as Claim.ClaimItem.Model));

  readonly claim$ = this.claimSvc.getClaim();

  readonly isCustomerPortal$ = this.route.data.pipe(map(data => data['isCustomerPortal'] as boolean));

  readonly customerCanEdit$ = combineLatest([this.isCustomerPortal$, this.item$, this.claim$]).pipe(
    map(([isCustomerPortal, item, claim]) => {
      if (
        isCustomerPortal &&
        item.status === ClaimItem.Status.DRAFT &&
        claim?.status === Claim.Status.OPEN &&
        !item.authorizationId
      )
        return true;
      return false;
    })
  );

  canManageCoverage$ = combineLatest([this.claim$, this.isCustomerPortal$]).pipe(
    switchMap(async ([claim, isCustomerPortal]) => {
      if (isCustomerPortal) return false;

      const permissionKey = claim?.warranty?.distributor?.permissionKey
        ? [claim?.warranty?.distributor?.permissionKey]
        : null;
      return await this.securitySvc.hasAccess('clm_ManageClaimItemCure', permissionKey);
    })
  );

  readonly canAdjust$ = this.staffAccess(Claim.ClaimItem.ADJUST_LINE_PERMISSION);
  readonly canManageHolds$ = this.staffAccess(Claim.ClaimItem.MANAGE_HOLDS_PERMISSION);
  readonly canAttachEvidence$ = this.staffAccess(Claim.ClaimItem.ATTACH_HOLD_EVIDENCE_PERMISSION);
  readonly canViewFiles$ = this.staffAccess('clm_ViewAttachments');
  readonly canUploadFiles$ = this.staffAccess('clm_ManageAttachments');

  linksAndConfigs$ = this.item$.pipe(
    tap(item => (this.itemId = item.id)),
    switchMap(async item =>
      item
        ? await this.linkSvc.list(
            { id: item.id, objectType: Link.ObjectType.CLAIM_ITEM },
            { owner: { id: item.id, objectType: Link.ObjectType.CLAIM_ITEM } }
          )
        : null
    ),
    map(data => data?.items),
    map(links =>
      links?.reduce(
        (acc, link) => {
          if (!this.itemId) return acc;

          const type = Link.getLinkType(link, Link.ObjectType.CLAIM_ITEM);
          if (type === Link.ObjectType.ACTIVITY) return acc;

          const object = Link.getLinkObject(link, type, this.itemId);
          if ((object as any)?.deleted) return acc;

          const config = Link.getLinkInfoCardConfig(link, type, this.itemId);
          acc.push({ link, config });

          return acc;
        },
        [] as { link: Link.Model; config: InfoCardConfig }[]
      )
    )
  );

  /** Staff only, scoped to the claim's distributor. */
  private staffAccess(
    permission:
      | typeof Claim.ClaimItem.ADJUST_LINE_PERMISSION
      | typeof Claim.ClaimItem.ATTACH_HOLD_EVIDENCE_PERMISSION
      | 'clm_ViewAttachments'
      | 'clm_ManageAttachments'
  ) {
    return combineLatest([this.claim$, this.isCustomerPortal$]).pipe(
      switchMap(async ([claim, isCustomerPortal]) => {
        if (isCustomerPortal) return false;

        const permissionKey = claim?.warranty?.distributor?.permissionKey
          ? [claim.warranty.distributor.permissionKey]
          : null;
        return await this.securitySvc.hasAccess(permission, permissionKey);
      })
    );
  }

  /** Removing a charge can cancel its holds, so reload them. */
  refreshHolds() {
    this.holdsPanel?.refresh();
  }

  getItemTheme(item?: Claim.ClaimItem.Model) {
    return item ? Claim.ClaimItem.getTheme(item.status) : 'default';
  }

  navigateTo(path?: string) {
    this.router.navigate([`./${path}`], { relativeTo: this.route });
  }
}
